import type { AppContext } from '@application/context/AppContext';
import type { ActivationClaims } from '@infra/licence/ActivationClaims';
import {
  claimsMatchBinding,
  parseLicenceBinding,
  type LicenceBinding,
} from '@infra/licence/LicenceBinding';
import { SecureLicenceStore } from '@infra/licence/SecureLicenceStore';
import { logger } from '@infra/telemetry/Logger';
import type { Tenant } from '@shared/types/Tenant';
import { TenantProvisioningService } from './TenantProvisioningService';

/**
 * Point de contrôle unique avant l'enregistrement d'un certificat.
 *
 * Règles :
 * - Plusieurs licences LOCAL_OFFLINE distinctes (1 clé = 1 entreprise) : autorisées
 *   pour les cabinets / multi-dossiers sur le même poste.
 * - Licences Cabinet signées : N tenants du même `cabinet_group_id`.
 * - Mode distant : une seule entreprise PME par poste sauf claim cabinet.
 */
export async function assertActivationAllowed(
  ctx: AppContext,
  claims: ActivationClaims,
): Promise<string | null> {
  const binding = parseLicenceBinding(await SecureLicenceStore.getLicenceBinding());
  if (binding && !claimsMatchBinding(claims, binding)) {
    logger.warn({
      event: 'activation_customer_changed',
      previousCustomer: binding.customer_id,
      nextCustomer: claims.customer_id,
    });
  }

  const portfolio = await SecureLicenceStore.getActivationPortfolio();
  const portfolioTenantIds = Object.keys(portfolio);
  const activeJwt = await SecureLicenceStore.getActivationCertificate();

  const tenants = await ctx.localTables.list<Tenant>('tenants');
  const realTenants = tenants.filter(
    (tenant) => !TenantProvisioningService.isDemoTenant(tenant.id),
  );

  const isCabinetClaim = claims.cabinet === true && typeof claims.cabinet_group_id === 'string';
  const isLocalOffline = claims.data_mode === 'LOCAL_OFFLINE';

  // Premier JWT du portefeuille (si map vide mais cert actif déjà présent)
  if (portfolioTenantIds.length === 0 && activeJwt) {
    const activeTenant = binding?.tenant_id;
    if (activeTenant && activeTenant !== claims.tenant_id) {
      if (isCabinetClaim) {
        if (!binding?.cabinet_group_id) {
          return 'Ce poste est déjà activé pour une autre entreprise. Mode Multi-entités réservé aux licences Cabinet ou au mode local multi-dossiers.';
        }
        if (claims.cabinet_group_id !== binding.cabinet_group_id) {
          return 'Cette licence appartient à un autre portefeuille cabinet.';
        }
        return null;
      }
      if (isLocalOffline) {
        // Multi-dossiers locaux : 1 clé commerciale par entreprise
        return null;
      }
      return 'Ce poste est déjà activé pour une autre entreprise. Mode Multi-entités réservé aux licences Cabinet ou au mode local multi-dossiers.';
    }
  }

  if (portfolioTenantIds.length > 0) {
    const sameTenant = portfolio[claims.tenant_id];
    if (sameTenant) {
      // Renouvellement / réactivation du même tenant : OK
      return null;
    }

    if (isCabinetClaim) {
      const boundGroup =
        binding?.cabinet_group_id ??
        (await inferCabinetGroupFromPortfolio(portfolio, claims.tenant_id));
      if (!boundGroup) {
        return 'Portefeuille cabinet incohérent. Contactez le support Nexxus.';
      }
      if (claims.cabinet_group_id !== boundGroup) {
        return 'Cette licence n’appartient pas au même portefeuille cabinet.';
      }
      return null;
    }

    if (isLocalOffline) {
      return null;
    }

    return 'Impossible d’ajouter une licence PME distante sur un poste déjà activé. Utilisez une licence Multi-entités / Cabinet, ou le mode local.';
  }

  // Données locales d’un autre tenant sans claim cabinet = refus (hors multi local)
  const hasForeignTenant = realTenants.some((tenant) => tenant.id !== claims.tenant_id);
  if (
    hasForeignTenant &&
    !isCabinetClaim &&
    !isLocalOffline &&
    (portfolioTenantIds.length > 0 || activeJwt)
  ) {
    const onlySelf =
      realTenants.every((t) => t.id === claims.tenant_id) ||
      (binding?.tenant_id === claims.tenant_id && portfolioTenantIds.length <= 1);
    if (!onlySelf && binding?.tenant_id && binding.tenant_id !== claims.tenant_id) {
      return 'Ce poste contient déjà les données d’une autre entreprise.';
    }
  }

  return null;
}

async function inferCabinetGroupFromPortfolio(
  _portfolio: Record<string, string>,
  _excludeTenant: string,
): Promise<string | null> {
  const binding = parseLicenceBinding(await SecureLicenceStore.getLicenceBinding());
  return binding?.cabinet_group_id ?? null;
}

export async function persistLicenceBinding(claims: ActivationClaims): Promise<void> {
  const binding: LicenceBinding = {
    customer_id: claims.customer_id,
    tenant_id: claims.tenant_id,
    ...(claims.cabinet && claims.cabinet_group_id
      ? { cabinet_group_id: claims.cabinet_group_id }
      : {}),
  };
  await SecureLicenceStore.setLicenceBinding(JSON.stringify(binding));
}
